import { useEffect, useRef } from "react";
import { MultiSelectOptions } from "../../../../internal/components/MultiSelectOptions";
import { OptionsPanelHandle } from "../../../../internal/components/OptionsPanel";
import { MultiSelectFieldTrigger } from "./MultiSelectFieldTrigger";
import { SelectFieldLabel } from "../../SelectField/internal/SelectFieldLabel";
import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import { useSelectModeMultiple } from "./useSelectModeMultiple";
import { OptionsPopover } from "../../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../../internal/components/OptionsDialog/OptionsDialog";
import { FieldMessage } from "../../../../components/FieldMessage";
import { MultiSelectFieldBaseInternalProps } from "./types";

/**
 * Select mode: non-searchable multi-select using useSelect.
 * Active when disableSearch is true.
 */
export const MultiSelectFieldSelectMode = ({
  labelAiMark,
  id,
  helperUid,
  selectedOptions,
  onSelectedOptionsChange,
  displayAs,
  isDisabledOrReadOnly,
  disabled,
  readOnly,
  required,
  placeholder,
  size,
  error,
  hasHelperText,
  disableClearButton,
  prefix,
  suffix,
  label,
  hideLabel,
  hint,
  errorMessages,
  warning,
  description,
  className,
  style,
  layoutStyles,
  pinned,
  groupToString,
  groupSorter,
  virtualize,
  options,
  loading,
  loadingMore,
  hasMore,
  loadOptions,
  loadMore,
  initialLoad,
  initialLoadPerformed,
  setInitialLoadPerformed,
  inputWrapperRef,
  selectAll,
  singleRow,
  maxChips,
  getChipProps,
  handleRemoveOption,
  handleClear,
}: MultiSelectFieldBaseInternalProps) => {
  const listRef = useRef<OptionsPanelHandle>(null);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    wrapperDivRef,
    highlightedIndex,
    getItemProps,
    items,
    sectionsMeta,
    closeMenu,
  } = useSelectModeMultiple({
    displayAs,
    onSelectedOptionsChange,
    disabled: isDisabledOrReadOnly,
    options,
    pinned,
    virtualize,
    groupToString,
    groupSorter,
    selectedOptions,
    selectAll,
    async onIsOpenChange(changes) {
      if (initialLoad === "open" && !initialLoadPerformed && changes.isOpen) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    },
    onScrollToHighlight: (i) => listRef.current?.scrollToIndex(i),
  });

  useEffect(() => {
    const performInitialLoad = async () => {
      if (initialLoad === "immediate" && !initialLoadPerformed) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    };
    performInitialLoad();
  }, [loadOptions, initialLoadPerformed, initialLoad, setInitialLoadPerformed]);

  const handleLoadMore = async () => {
    await loadMore("");
  };

  const toggleButtonProps = getToggleButtonProps({
    disabled: !!disabled,
    "aria-required": required ? true : undefined,
    "aria-describedby": hasHelperText ? helperUid : undefined,
    "aria-invalid": error ? true : undefined,
  } as Record<string, unknown>);

  if (displayAs === "dialog") {
    return (
      <div
        className={
          className
            ? `${styles["search-field"]} ${className}`
            : styles["search-field"]
        }
        style={{ ...layoutStyles, ...style }}
        ref={wrapperDivRef}
      >
        <SelectFieldLabel
          aiMark={labelAiMark}
          label={label}
          hideLabel={hideLabel}
          getLabelProps={getLabelProps}
          required={required}
        />
        <MultiSelectFieldTrigger
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          disableClearButton={disableClearButton}
          toggleButtonProps={toggleButtonProps}
          onClear={handleClear}
          id={`${id}-trigger`}
          inputWrapperRef={inputWrapperRef}
          size={size}
          error={!!error}
          prefix={prefix}
          suffix={suffix}
          selectedOptions={selectedOptions}
          onRemoveOption={handleRemoveOption}
          singleRow={singleRow}
          maxChips={maxChips}
          getChipProps={getChipProps}
        />
        {hasHelperText ? (
          <FieldMessage
            id={helperUid}
            hint={hint}
            error={errorMessages}
            warning={warning}
            description={description}
          />
        ) : null}
        <OptionsDialog
          id={`${id}-dialog`}
          isOpen={isOpen}
          onClose={closeMenu}
          title={label}
        >
          <MultiSelectOptions
            ref={listRef}
            isOpen
            items={items}
            getMenuProps={(opts) =>
              getMenuProps({
                ...opts,
                style: { ...opts?.style, outline: "none" },
                onKeyDown: toggleButtonProps.onKeyDown,
                tabIndex: 0,
                "aria-activedescendant":
                  toggleButtonProps["aria-activedescendant"],
              })
            }
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOptions={selectedOptions}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            disabled={isDisabledOrReadOnly}
            sectionsMeta={sectionsMeta}
            virtualize={virtualize}
          />
        </OptionsDialog>
      </div>
    );
  }

  return (
    <div
      className={
        className
          ? `${styles["search-field"]} ${className}`
          : styles["search-field"]
      }
      style={{ ...layoutStyles, ...style }}
    >
      <SelectFieldLabel
        aiMark={labelAiMark}
        label={label}
        hideLabel={hideLabel}
        getLabelProps={getLabelProps}
        required={required}
      />
      <MultiSelectFieldTrigger
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        disableClearButton={disableClearButton}
        toggleButtonProps={toggleButtonProps}
        onClear={handleClear}
        id={`${id}-trigger`}
        popoverTarget={`${id}-popover`}
        inputWrapperRef={inputWrapperRef}
        size={size}
        error={!!error}
        prefix={prefix}
        suffix={suffix}
        selectedOptions={selectedOptions}
        onRemoveOption={handleRemoveOption}
        singleRow={singleRow}
        maxChips={maxChips}
        getChipProps={getChipProps}
      />
      {hasHelperText ? (
        <FieldMessage
          id={helperUid}
          hint={hint}
          error={errorMessages}
          warning={warning}
          description={description}
        />
      ) : null}
      <OptionsPopover
        id={`${id}-popover`}
        referenceElement={inputWrapperRef}
        open={isOpen && displayAs === "popover"}
        onClickOutside={closeMenu}
        width="reference"
      >
        <MultiSelectOptions
          ref={listRef}
          isOpen={isOpen}
          items={items}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          selectedOptions={selectedOptions}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          loading={loading}
          loadingMore={loadingMore}
          disabled={isDisabledOrReadOnly}
          sectionsMeta={sectionsMeta}
          virtualize={virtualize}
        />
      </OptionsPopover>
    </div>
  );
};
